// Consent constants + helpers for the admin console.
//
// Design note: the admin console is a signed-in B2B tool. Every user who
// reaches these screens has intentionally created an account and accepted
// the Terms of Service + Privacy Policy on signup. There are no
// advertising / third-party analytics on the console — the only telemetry
// is Sentry error + performance monitoring, which is operationally
// essential for us to keep the service running (a legitimate interest and
// arguably strictly necessary under the ePrivacy Directive).
//
// Because of that, the console does NOT expose per-category opt-in/out
// controls. Instead we show a one-time informational notice ("we use
// cookies, here's what and why") and record the acknowledgment so we have
// an audit trail. Cookie/Privacy details live at artistintro.com/*.
//
// If we ever add optional non-essential telemetry (product analytics,
// marketing) we should reintroduce per-category toggles at that point.

import axios from '../utils/axiosInstance';

// Bumping this string invalidates all stored consent records and re-shows
// the auth-page banner on next visit. Keep in sync with the artist-website
// value so the platform policy is one shared version string.
export const POLICY_VERSION = '2026-07-06';

// LocalStorage keys — strictly necessary. Distinct from the artist-website
// keys because storage is per-origin but the constants shouldn't overlap
// anyway.
export const CONSENT_ID_KEY = 'ai_admin_consent_id';
export const CONSENT_STORAGE_KEY = 'ai_admin_consent_v1';

// Platform-wide legal URLs. Admin console never renders these routes; it
// only links out.
export const LEGAL_URLS = {
  terms: 'https://artistintro.com/termsofservice',
  privacy: 'https://artistintro.com/privacypolicy',
  cookies: 'https://artistintro.com/cookiepolicy',
} as const;

// The admin console only records acknowledgement of the cookie notice, so
// only `accept_all` is ever emitted here. The wider enum is kept in sync
// with the backend schema (which is shared with the artist-website).
export type ConsentAction =
  | 'accept_all'
  | 'reject_all'
  | 'custom'
  | 'withdrawn';

export type ConsentPage = 'login' | 'signup' | 'invite' | 'sidebar' | 'banner';

// The console has no user-toggleable categories at this time. `choices` is
// kept as an empty-object shape so we can add categories later without a
// schema migration on either the client or the backend log.
export type ConsentChoices = Record<string, never>;

export interface StoredConsent {
  version: 1;
  policyVersion: string;
  choices: ConsentChoices;
  action: ConsentAction;
  updatedAt: string;
}

export const EMPTY_CHOICES: ConsentChoices = {};

const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  return `fallback-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const getOrCreateConsentId = (): string => {
  try {
    let id = localStorage.getItem(CONSENT_ID_KEY);
    if (!id) {
      id = generateUUID();
      localStorage.setItem(CONSENT_ID_KEY, id);
    }
    return id;
  } catch {
    return generateUUID();
  }
};

export const readStoredConsent = (): StoredConsent | null => {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredConsent>;
    if (!parsed || parsed.version !== 1) return null;
    if (parsed.policyVersion !== POLICY_VERSION) return null;
    if (!parsed.action) return null;
    return { ...(parsed as StoredConsent), choices: EMPTY_CHOICES };
  } catch {
    return null;
  }
};

export const writeStoredConsent = (record: StoredConsent): void => {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Storage may be unavailable — silently continue.
  }
};

interface PostConsentArgs {
  consentId: string;
  choices: ConsentChoices;
  action: ConsentAction;
  page: ConsentPage;
  /** True when a JWT is present. Determines endpoint + shape. */
  authenticated: boolean;
}

/**
 * Fire-and-forget POST to record the consent event. Uses the public endpoint
 * when the user is not yet authenticated (login/signup/invite screens) and
 * the authenticated endpoint once a JWT is available so the row is attributed
 * to the actual user. Never blocks the UI.
 */
export const postConsent = async (args: PostConsentArgs): Promise<void> => {
  try {
    if (args.authenticated) {
      // axiosInstance auto-attaches the bearer token from storage.
      await axios.post('/api/consent', {
        consentId: args.consentId,
        choices: args.choices,
        action: args.action,
        policyVersion: POLICY_VERSION,
        page: args.page,
      });
    } else {
      // Pre-auth: use the public endpoint. axiosInstance is fine (it just
      // sends without an Authorization header when no token is stored).
      // `source: admin-console` tells the shared public endpoint to attribute
      // the row to this app rather than defaulting to artist-website.
      await axios.post('/api/public/consent', {
        consentId: args.consentId,
        tenantSlug: null,
        source: 'admin-console',
        choices: args.choices,
        action: args.action,
        policyVersion: POLICY_VERSION,
        page: args.page,
      });
    }
  } catch (err) {
    // Non-blocking — the local state is authoritative for UX. Log failures
    // so misconfigured backend URLs / expired tokens / CORS surface in dev
    // tools instead of silently dropping the audit event.
    if (typeof console !== 'undefined') {
      console.warn('[consent] backend log failed', err);
    }
  }
};
